// SPDX-License-Identifier: AGPL-3.0-or-later

import Presence from '@app/features/presence/state/Presence';
import RuntimeConfig from '@app/features/app/state/RuntimeConfig';
import {AvatarDecorationMedia} from '@app/features/user/components/AvatarDecorationMedia';
import {DecorationFxCanvas, hasDecorationFx} from '@app/features/cosmetics/fx/FxCanvas';
import styles from '@app/features/user/components/AvatarDecorationOverlay.module.css';
import AvatarDecorationCatalog from '@app/features/user/state/AvatarDecorationCatalog';
import Users from '@app/features/user/state/Users';
import {getAvatarDecorationStatusCutoutStyle} from '@app/features/user/utils/AvatarDecorationCutout';
import {isOfflineStatus} from '@fluxer/constants/src/StatusConstants';
import {observer} from 'mobx-react-lite';
import {useEffect, useMemo} from 'react';

interface AvatarDecorationOverlayProps {
	userId: string;
	decorationId?: string | null;
	className?: string;
	active?: boolean;
	avatarSize?: number;
	statusCutout?: boolean;
	isTyping?: boolean;
}

export const AvatarDecorationOverlay = observer(function AvatarDecorationOverlay({
	userId,
	decorationId,
	className,
	active = true,
	avatarSize,
	statusCutout = false,
	isTyping = false,
}: AvatarDecorationOverlayProps) {
	useEffect(() => {
		void AvatarDecorationCatalog.load();
	}, []);
	const equippedId = decorationId ?? Users.getAvatarDecorationId(userId) ?? null;
	const entry = AvatarDecorationCatalog.getById(equippedId);
	const isMobile = Boolean(userId) && Presence.isMobile(userId);
	const cutoutStyle = useMemo(() => {
		if (!statusCutout || avatarSize == null) {
			return undefined;
		}
		return getAvatarDecorationStatusCutoutStyle({
			avatarSize,
			isMobile,
			isTyping,
		});
	}, [avatarSize, isMobile, isTyping, statusCutout]);
	if (!RuntimeConfig.avatarDecorationsEnabled || !entry) {
		return null;
	}
	return (
		<div className={className ?? styles.overlay} style={cutoutStyle} aria-hidden="true">
			{hasDecorationFx(entry.fx) ? (
				<DecorationFxCanvas fxKey={entry.fx} active={active} className={styles.image} />
			) : (
				<AvatarDecorationMedia src={entry.image} className={styles.image} active={active} />
			)}
		</div>
	);
});

export function shouldShowAvatarDecorationStatusCutout(status: string | null | undefined, showOffline: boolean): boolean {
	if (status == null) {
		return false;
	}
	if (isOfflineStatus(status)) {
		return showOffline;
	}
	return true;
}
